import Typo from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import type { TypographyProps } from '@mui/material'

import type { QuantityDiscount } from 'src/shared/discountShop'
import {
  getQuantityDiscountText,
} from 'src/functions/quantityDiscountText'
import type { QuantityDiscountText } from 'src/functions/quantityDiscountText'
import { useDiscountDateActive } from 'src/hooks/useDiscountDateActive'

type Props = Omit<TypographyProps, 'children'> & {
  quantityDiscount?: QuantityDiscount
  qty?: number
  className?: string
  text?: QuantityDiscountText
  dateEnd?: Date | string | number | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: 'rgba(194, 0, 106, .07)',
      border: '1px solid rgba(194, 0, 106, .24)',
      borderRadius: 6,
      boxSizing: 'border-box',
      color: theme.palette.text.primary,
      display: 'grid',
      gap: theme.spacing(.35),
      justifyItems: 'center',
      lineHeight: 1.3,
      maxWidth: '100%',
      padding: theme.spacing(.7, 1),
      whiteSpace: 'normal',
    },
    rootActive: {
      backgroundColor: 'rgba(23, 143, 70, .1)',
      borderColor: 'rgba(23, 143, 70, .36)',
      color: theme.palette.success.dark,
    },
    eyebrow: {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      color: theme.palette.primary.contrastText,
      display: 'inline-flex',
      fontSize: '.68rem',
      fontWeight: 900,
      letterSpacing: 0,
      lineHeight: 1,
      padding: theme.spacing(.3, .65),
      textTransform: 'uppercase',
    },
    eyebrowActive: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    title: {
      color: theme.palette.primary.dark,
      fontWeight: 900,
      lineHeight: 1.2,
    },
    progress: {
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
    },
    active: {
      color: theme.palette.success.main,
    },
  })
)

const QuantityDiscountInfo: React.FC<Props> = ({
  quantityDiscount,
  qty,
  className,
  text,
  dateEnd,
  component = 'div',
  variant = 'body2',
  align,
  ...rest
}) => {
  const classes = useStyles()
  const discountDateActive = useDiscountDateActive(dateEnd)
  if (!discountDateActive) return null

  const resolvedText = text || getQuantityDiscountText(quantityDiscount, qty)
  if (!resolvedText) return null

  return (
    <div className={[
      classes.root,
      resolvedText.active ? classes.rootActive : null,
      className,
    ].filter(Boolean).join(' ')}>
      <span className={[
        classes.eyebrow,
        resolvedText.active ? classes.eyebrowActive : null,
      ].filter(Boolean).join(' ')}>
        Aktion
      </span>
      <Typo
        {...rest}
        component={component}
        variant={variant}
        align={align}
        className={classes.title}
      >
        {resolvedText.title}
      </Typo>
      {resolvedText.progress && (
        <Typo
          {...rest}
          component={component}
          variant={variant}
          align={align}
          className={[
            classes.progress,
            resolvedText.active ? classes.active : null,
          ].filter(Boolean).join(' ')}
        >
          {resolvedText.progress}
        </Typo>
      )}
    </div>
  )
}

export default QuantityDiscountInfo
